import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import ErrorRounded from "@mui/icons-material/ErrorRounded";
import WarningRounded from "@mui/icons-material/WarningRounded";
import HelpRounded from "@mui/icons-material/HelpRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import Loading from "components/Loading";
import { useAuth } from "hooks/useAuth";
import firebaseApp, { db } from "services/firebase";
import { differenceInMinutes, formatDistanceToNow } from "date-fns";

/**
 * System Health — global-admin view of `systemHealth/status` and
 * `systemHealth/ci`.
 *
 * `status` is maintained by the hourly check in
 * functions/src/scheduled/systemHealth.js; `ci` is written by the E2E workflow
 * via scripts/ci/publishTestResults.js.
 *
 * Nothing is computed on the client except staleness: a check that stopped
 * running must never keep reading green, so the freshness of `lastCheckedAt`
 * decides the colour, not the stored status alone.
 */

/** Checks run hourly; allow three misses before calling a check unreliable. */
const STALE_AFTER_MINUTES = 180;

/** Expected services, so one that has never run still gets a row. */
const EXPECTED_SERVICES = [
  { key: "openai", label: "OpenAI" },
  { key: "anthropic", label: "Anthropic (Claude)" },
  { key: "google", label: "Google (Gemini)" },
  { key: "email", label: "Email delivery" },
];

const STATE_OK = "ok";
const STATE_WARN = "warn";
const STATE_DOWN = "down";
const STATE_STALE = "stale";
const STATE_UNKNOWN = "unknown";

const PRESENTATION = {
  [STATE_OK]: {
    label: "Operational",
    color: "success.main",
    chipColor: "success",
    Icon: CheckCircleRounded,
  },
  [STATE_WARN]: {
    label: "Degraded",
    color: "warning.main",
    chipColor: "warning",
    Icon: WarningRounded,
  },
  [STATE_DOWN]: {
    label: "Down",
    color: "error.main",
    chipColor: "error",
    Icon: ErrorRounded,
  },
  [STATE_STALE]: {
    label: "Not reporting",
    color: "warning.main",
    chipColor: "warning",
    Icon: WarningRounded,
  },
  [STATE_UNKNOWN]: {
    label: "Never checked",
    color: "text.disabled",
    chipColor: "default",
    Icon: HelpRounded,
  },
};

const ROLLUP = {
  [STATE_DOWN]: {
    title: "Something is down",
    detail: "At least one service failed its most recent check.",
  },
  [STATE_WARN]: {
    title: "Needs a look",
    detail: "A service is degraded or has stopped reporting in.",
  },
  [STATE_OK]: {
    title: "All systems operational",
    detail: "Every service passed its most recent check.",
  },
  [STATE_UNKNOWN]: {
    title: "Waiting for the first check",
    detail: "No health data has been recorded yet.",
  },
};

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  return null;
};

const relative = (value) => {
  const date = toDate(value);
  if (!date) return "never";
  return formatDistanceToNow(date, { addSuffix: true });
};

/**
 * Fold the stored status together with how long ago it was written.
 * Stale beats a stored "ok" — silence is not health.
 */
const resolveState = (entry) => {
  if (!entry?.status) return STATE_UNKNOWN;

  const checkedAt = toDate(entry.lastCheckedAt);
  if (!checkedAt) return STATE_UNKNOWN;

  if (differenceInMinutes(new Date(), checkedAt) > STALE_AFTER_MINUTES) {
    return STATE_STALE;
  }

  if (entry.status === STATE_DOWN) return STATE_DOWN;
  if (entry.status === STATE_WARN) return STATE_WARN;
  return STATE_OK;
};

const StatusDot = ({ state, size = 10 }) => (
  <Box
    aria-hidden
    sx={{
      width: size,
      height: size,
      borderRadius: "50%",
      flexShrink: 0,
      bgcolor: PRESENTATION[state].color,
    }}
  />
);

/** One row in the services list. Reads left to right: what, how, when. */
const ServiceRow = ({ label, entry, state }) => {
  const presentation = PRESENTATION[state];

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={{ xs: 1, sm: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
          <StatusDot state={state} />
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={600} noWrap>
              {label}
            </Typography>
            {entry?.detail ? (
              <Typography variant="body2" color="text.secondary">
                {entry.detail}
              </Typography>
            ) : null}
          </Box>
        </Stack>

        {/* Deliberately just the status chip. No latency — "5204 ms" reads as
            alarming to a non-technical reader and is not actionable (it is
            still recorded in Firestore). No timestamp either: one sweep checks
            every service, so the hero's "last checked" covers all of them. */}
        <Tooltip
          title={
            state === STATE_STALE
              ? `This check last reported ${relative(entry?.lastCheckedAt)} and is overdue, so its status cannot be trusted`
              : ""
          }
        >
          <Chip
            size="small"
            label={presentation.label}
            color={presentation.chipColor}
            sx={{ flexShrink: 0, ml: { xs: 3, sm: 0 } }}
          />
        </Tooltip>
      </Stack>

      {entry?.lastError ? (
        <Typography variant="body2" color="error.main" sx={{ mt: 1, pl: 3 }}>
          {entry.lastError}
        </Typography>
      ) : null}
    </Box>
  );
};

/** Results for one CI suite, with a collapsible per-test list. */
const TestSuitePanel = ({ suite }) => {
  const [open, setOpen] = useState(false);

  const state = suite.status === "passed" ? STATE_OK : STATE_DOWN;
  const specs = Array.isArray(suite.specs) ? suite.specs : [];
  const shortCommit = suite.commit ? suite.commit.slice(0, 7) : null;

  return (
    <Box>
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={{ xs: 1, sm: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
            <StatusDot state={state} />
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={600}>{suite.label}</Typography>
              <Typography
                variant="body2"
                color={suite.problem ? "error.main" : "text.secondary"}
              >
                {/* `problem` covers runs that never got as far as executing a
                    test. Printing "0 of 0 passed" for those would read as a
                    clean result. */}
                {suite.problem
                  ? suite.problem
                  : `${suite.passed} of ${suite.total} passed${
                      suite.skipped ? `, ${suite.skipped} skipped` : ""
                    }${suite.failed ? `, ${suite.failed} failed` : ""}`}
                {" · "}
                {relative(suite.finishedAt)}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
            {suite.branch ? <Chip size="small" label={suite.branch} /> : null}
            {shortCommit ? (
              <Tooltip title={suite.commitMessage || ""}>
                <Chip size="small" variant="outlined" label={shortCommit} />
              </Tooltip>
            ) : null}
            {suite.runUrl ? (
              <Tooltip title="Open this run on GitHub">
                <IconButton
                  size="small"
                  component={Link}
                  href={suite.runUrl}
                  target="_blank"
                  rel="noopener"
                >
                  <OpenInNewRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            {specs.length ? (
              <IconButton
                size="small"
                onClick={() => setOpen((value) => !value)}
                aria-label={open ? "Hide tests" : "Show tests"}
              >
                <ExpandMoreRounded
                  fontSize="small"
                  sx={{
                    transform: open ? "rotate(180deg)" : "none",
                    transition: "transform 150ms",
                  }}
                />
              </IconButton>
            ) : null}
          </Stack>
        </Stack>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            pb: 2,
            maxHeight: 360,
            overflowY: "auto",
          }}
        >
          <Stack spacing={0.75}>
            {specs.map((spec) => (
              <Stack
                key={spec.title}
                direction="row"
                alignItems="flex-start"
                spacing={1.5}
              >
                {spec.status === "passed" ? (
                  <CheckCircleRounded sx={{ fontSize: 16, color: "success.main", mt: 0.25 }} />
                ) : spec.status === "skipped" ? (
                  <HelpRounded sx={{ fontSize: 16, color: "text.disabled", mt: 0.25 }} />
                ) : (
                  <ErrorRounded sx={{ fontSize: 16, color: "error.main", mt: 0.25 }} />
                )}
                <Typography
                  variant="body2"
                  color={spec.status === "failed" ? "error.main" : "text.secondary"}
                >
                  {spec.title}
                </Typography>
              </Stack>
            ))}
            {suite.truncatedSpecs ? (
              <Typography variant="caption" color="text.secondary">
                and {suite.truncatedSpecs} more
              </Typography>
            ) : null}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};

/**
 * One environment's last deploy, from the Firebase Hosting release list.
 *
 * Deploys are manual today, so Hosting is the only thing that sees them and
 * there is no commit to report. The branch/commit/run chips render if those
 * fields ever appear — see functions/src/scheduled/deployments.js.
 */
const DeploymentRow = ({ environment }) => {
  const shortCommit = environment.commit ? environment.commit.slice(0, 7) : null;

  if (environment.accessible === false) {
    return (
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <StatusDot state={STATE_UNKNOWN} />
          <Box>
            <Typography fontWeight={600} color="text.secondary">
              {environment.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Not connected. This environment needs read access before its
              deploys can be shown here.
            </Typography>
          </Box>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={{ xs: 1, sm: 2 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography fontWeight={600}>{environment.label}</Typography>
          <Typography variant="body2" color="text.secondary">
            {environment.releasedAt
              ? `Last updated ${relative(environment.releasedAt)}`
              : "No deploys recorded"}
            {environment.deployTool ? ` via ${environment.deployTool}` : ""}
            {environment.releasedBy ? ` by ${environment.releasedBy}` : ""}
          </Typography>
          {environment.error ? (
            <Typography variant="body2" color="error.main">
              {environment.error}
            </Typography>
          ) : null}
        </Box>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          {environment.branch ? (
            <Chip size="small" label={environment.branch} />
          ) : null}
          {shortCommit ? (
            <Tooltip title={environment.commitMessage || ""}>
              <Chip size="small" variant="outlined" label={shortCommit} />
            </Tooltip>
          ) : null}
          {environment.runUrl ? (
            <Tooltip title="Open the deploy on GitHub">
              <IconButton
                size="small"
                component={Link}
                href={environment.runUrl}
                target="_blank"
                rel="noopener"
              >
                <OpenInNewRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
};

/** Shared section wrapper: heading above a single bordered surface. */
const Section = ({ title, action, children }) => (
  <Box sx={{ mb: 4 }}>
    <Stack
      direction="row"
      alignItems="baseline"
      justifyContent="space-between"
      sx={{ mb: 1.5 }}
    >
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
      {action}
    </Stack>
    <Paper>{children}</Paper>
  </Box>
);

const SystemHealthPage = () => {
  const { isGlobalAdmin, userLoading } = useAuth();
  const [health, setHealth] = useState(null);
  const [ci, setCi] = useState(null);
  const [deploys, setDeploys] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Relative timestamps go stale on screen; re-render every minute so
  // "checked 22 hours ago" stays honest without a manual refresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((value) => value + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isGlobalAdmin) return undefined;

    const collection = db.collection("systemHealth");

    const unsubscribeStatus = collection.doc("status").onSnapshot(
      (snapshot) => {
        setHealth(snapshot.exists ? snapshot.data() : null);
        setError(null);
        setLoading(false);
      },
      (snapshotError) => {
        setError(snapshotError.message || "Failed to load system health.");
        setLoading(false);
      }
    );

    // Both are absent until their first publish; those sections stay hidden.
    const unsubscribeCi = collection
      .doc("ci")
      .onSnapshot((snapshot) => setCi(snapshot.exists ? snapshot.data() : null));

    const unsubscribeDeploys = collection
      .doc("deploys")
      .onSnapshot((snapshot) =>
        setDeploys(snapshot.exists ? snapshot.data() : null)
      );

    return () => {
      unsubscribeStatus();
      unsubscribeCi();
      unsubscribeDeploys();
    };
  }, [isGlobalAdmin]);

  const services = useMemo(() => {
    const stored = health?.services || {};

    // Anything the backend reports but this page does not know about still gets
    // a row, so adding a check server-side never needs a frontend release.
    const extraKeys = Object.keys(stored).filter(
      (key) => !EXPECTED_SERVICES.some((service) => service.key === key)
    );

    return [
      ...EXPECTED_SERVICES,
      ...extraKeys.map((key) => ({ key, label: stored[key]?.label || key })),
    ].map((service) => {
      const entry = stored[service.key] || null;
      return { ...service, entry, state: resolveState(entry) };
    });
  }, [health]);

  const rollupState = useMemo(() => {
    if (services.some((s) => s.state === STATE_DOWN)) return STATE_DOWN;
    if (services.some((s) => s.state === STATE_WARN || s.state === STATE_STALE)) {
      return STATE_WARN;
    }
    if (services.every((s) => s.state === STATE_UNKNOWN)) return STATE_UNKNOWN;
    return STATE_OK;
  }, [services]);

  const incidents = useMemo(
    () => (Array.isArray(health?.incidents) ? health.incidents : []),
    [health]
  );

  const hasEverFailed = useMemo(
    () => incidents.some((incident) => incident.status !== STATE_OK),
    [incidents]
  );

  const suites = useMemo(() => {
    const stored = ci?.suites || {};
    return Object.keys(stored).map((key) => ({ key, ...stored[key] }));
  }, [ci]);

  /**
   * Only the environment this page is actually served from. Showing every
   * environment invites the question "which one am I looking at?", which is
   * the opposite of what this panel is for. The Hosting site id equals the
   * Firebase project id for our environments, so the running config identifies
   * it without a separate mapping.
   */
  const currentEnvironment = useMemo(() => {
    const stored = deploys?.environments || {};
    const projectId = firebaseApp?.options?.projectId;
    if (!projectId) return null;

    const key = Object.keys(stored).find(
      (name) => stored[name]?.site === projectId || name === projectId
    );
    return key ? { key, ...stored[key] } : null;
  }, [deploys]);

  if (!userLoading && !isGlobalAdmin) {
    return <Navigate to="/dashboard/prompts" replace />;
  }

  const rollup = ROLLUP[rollupState];
  const RollupIcon = PRESENTATION[rollupState].Icon;

  return (
    <Box>
      <DashboardPageHeader
        title="System Health"
        subtitle="Live status of the outside services HealthDesk depends on, and the automated tests that run on every push."
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Loading small />
      ) : (
        <>
          {/* Hero band: the whole answer in one glance, colour-coded. */}
          <Paper
            sx={{
              p: { xs: 2.5, sm: 3 },
              mb: 4,
              borderLeftWidth: 6,
              borderLeftStyle: "solid",
              borderLeftColor: PRESENTATION[rollupState].color,
              bgcolor: (theme) =>
                rollupState === STATE_UNKNOWN
                  ? "transparent"
                  : alpha(
                      rollupState === STATE_OK
                        ? theme.palette.success.main
                        : rollupState === STATE_DOWN
                          ? theme.palette.error.main
                          : theme.palette.warning.main,
                      0.06
                    ),
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <RollupIcon
                  sx={{ fontSize: 44, color: PRESENTATION[rollupState].color }}
                />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {rollup.title}
                  </Typography>
                  <Typography color="text.secondary">{rollup.detail}</Typography>
                </Box>
              </Stack>

              {/* The single check timestamp for the whole page. Every service
                  is probed by one hourly sweep, so a per-row time would repeat
                  the same value four times over. */}
              {health?.updatedAt ? (
                <Box sx={{ textAlign: { xs: "left", sm: "right" }, pl: { xs: 7, sm: 0 } }}>
                  <Typography variant="body2" color="text.secondary">
                    Last checked
                  </Typography>
                  <Typography fontWeight={600}>
                    {relative(health.updatedAt)}
                  </Typography>
                </Box>
              ) : null}
            </Stack>

            {/* Only worth saying when it is good news. When something is wrong
                the banner above already says so and Recent changes has the
                detail, so repeating it here is noise. */}
            {!hasEverFailed && rollupState === STATE_OK ? (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  No problems recorded since monitoring started.
                </Typography>
              </>
            ) : null}
          </Paper>

          {rollupState === STATE_UNKNOWN ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              The scheduled check has not run yet. It runs hourly, so this page
              fills in on its own.
            </Alert>
          ) : null}

          <Section title="Services">
            {services.map((service, index) => (
              <React.Fragment key={service.key}>
                {index > 0 ? <Divider /> : null}
                <ServiceRow
                  label={service.label}
                  entry={service.entry}
                  state={service.state}
                />
              </React.Fragment>
            ))}
          </Section>

          {currentEnvironment ? (
            <Section title="This environment">
              <DeploymentRow environment={currentEnvironment} />
            </Section>
          ) : null}

          {suites.length ? (
            <Section
              title="Automated tests"
              action={
                <Typography variant="body2" color="text.secondary">
                  Runs on every push
                </Typography>
              }
            >
              {suites.map((suite, index) => (
                <React.Fragment key={suite.key}>
                  {index > 0 ? <Divider /> : null}
                  <TestSuitePanel suite={suite} />
                </React.Fragment>
              ))}
            </Section>
          ) : null}

          <Section title="Recent changes">
            {incidents.length === 0 ? (
              <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
                <Typography color="text.secondary">
                  Nothing to report. Entries appear here when a service changes
                  state.
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {incidents.map((incident, index) => {
                  const state =
                    incident.status === STATE_OK
                      ? STATE_OK
                      : incident.status === STATE_WARN
                        ? STATE_WARN
                        : STATE_DOWN;

                  return (
                    <React.Fragment
                      key={`${incident.service}-${toDate(incident.at)?.getTime() || index}`}
                    >
                      {index > 0 ? <Divider component="li" /> : null}
                      <ListItem sx={{ px: { xs: 2, sm: 3 } }}>
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          spacing={1.5}
                          sx={{ width: "100%" }}
                        >
                          <Box sx={{ pt: 1.25 }}>
                            <StatusDot state={state} />
                          </Box>
                          <ListItemText
                            primary={`${incident.label || incident.service} ${
                              incident.status === STATE_OK
                                ? "recovered"
                                : `went ${PRESENTATION[state].label.toLowerCase()}`
                            }`}
                            secondary={
                              <>
                                {relative(incident.at)}
                                {incident.alerted ? " · emailed to admins" : ""}
                                {incident.error ? `: ${incident.error}` : ""}
                              </>
                            }
                          />
                        </Stack>
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </Section>
        </>
      )}
    </Box>
  );
};

export default SystemHealthPage;
